import {Component} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {WorkflowService} from "../workflow.service";
import {AssetService} from "../../asset/asset.service";
import {SiteService} from "../../sites/site.service";
import {AssetRemovalService} from "../../asset/asset-removal.service";
import {WorkflowTargetInfoModel} from "../models/workflow-target-info.model";
import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {DartCorrectionRequestDialogComponent} from "./dart-correction-request";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {WorkflowStep} from "../workflow-step";
import {ConfirmDialogService} from "../../confirm-dialog/confirm-dialog-service";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {toggleLoading} from "../../misc/loading.decorator";
import {SiteDesignService} from "../../site-design/site-design.service";
import {AssetForSiteDesignModel} from "../../asset/asset-for-site-design.model";
import {AccessoryService} from "../../accessory/accessory.service";
import {FixAccessoryDialogComponent} from "./fix-accessory-dialog.component";
import {get} from "lodash";
import {ItsmAddressModel, ItsmSiteSettableAddressesModel} from "../../address/itsm-address.model";
import {ItsmContactModel} from "../../contact/itsm-contact.model";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  templateUrl: "./add-assets.template.pug",
  providers: [
    AssetService,
    AssetRemovalService,
    AccessoryService,
    DialogService,
    ConfirmDialogService,
    SiteDesignService,
    SiteService,
  ],
})
export class AddAssetsComponent extends WorkflowStep {
  public projectId: string;
  public siteId: string;
  public assets: AssetForSiteDesignModel[] = [];
  public currentPage: number = DEFAULT_PAGE;
  public pageSize: number = DEFAULT_PAGE_LIMIT;
  public maxPages: number = 0;
  public totalCount: number = 0;
  public searchText: string = "";
  public error: Error;
  public showImportCDF: boolean = false;
  public showManualDesign: boolean = false;
  public showAddIndividualAssets: boolean = false;
  public stepName: string = "";
  public targetSystem: string;
  public loading: boolean = true;

  // ITSM data
  public itsmDataReady = false;
  itsmAddressInfo: ItsmSiteSettableAddressesModel;
  settableAddresses: ItsmAddressModel[] = [];
  settableAddressesMap: any = {};
  settableAddressesWithItsmBuildingIds: ItsmAddressModel[] = []; // Only send these addresses to the dialog
  itsmSiteContacts: ItsmContactModel[] = [];
  itsmSiteContactsMap: any = {};
  itsmSiteContactsWithItsmId: ItsmContactModel[] = [];

  constructor(
    private siteDesignService: SiteDesignService,
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    public assetService: AssetService,
    public assetRemovalService: AssetRemovalService,
    public accessoryService: AccessoryService,
    private dialogService: DialogService,
    private confirmDialogService: ConfirmDialogService,
    private siteService: SiteService,
  ) {
    super();
  }

  @toggleLoading()
  @handleErrors()
  async ready(workflowTargetInfo: WorkflowTargetInfoModel) {
    this.loading = true;
    this.projectId = workflowTargetInfo.projectId;
    this.siteId = workflowTargetInfo.targetId;
    this.stepName = get(this.workflowService.currentStep, "stepName");
    this.showImportCDF = this.stepName.startsWith("importCdf");
    this.showManualDesign = this.stepName.startsWith("manualSiteDesign");
    this.showAddIndividualAssets = this.stepName.startsWith("addIndividualAssets");
    this.workflowService.setStepStatus(WorkflowStepStatus.Completed, workflowTargetInfo.stepName);
    await this.loadData();
    await this.loadItsmData();
    this.loading = false;
  }

  updateToReflectNewAddressOrContacts({addresses, contacts}) {
    addresses.forEach((it) => { if (!this.settableAddressesMap[it.id]) this.settableAddresses.push(it); });
    contacts.forEach((it) => { if (!this.itsmSiteContactsMap[it.id]) this.itsmSiteContacts.push(it); });
    this.updateItsmAddressMapsHelper();
    this.updateItsmContactMapsHelper();
  }

  updateItsmAddressMapsHelper() {
    this.settableAddresses.sort((a, b) => {
      if ((a.itsmName || "").toLowerCase() > (b.itsmName || "").toLowerCase()) return 1;
      return -1;
    });
    this.settableAddressesMap = this.settableAddresses.reduce((acc, it) => { acc[it.id] = it; return acc; }, {});
    this.settableAddressesWithItsmBuildingIds = this.settableAddresses.filter(it => it.itsmBuildingId);
  }

  updateItsmContactMapsHelper() {
    this.itsmSiteContacts.sort((a, b) => {
      if ((a.emailAddress || "").toLowerCase() > (b.emailAddress || "").toLowerCase()) return 1;
      return -1;
    });
    this.itsmSiteContactsMap = this.itsmSiteContacts.reduce((acc, it) => { acc[it.id] = it; return acc; }, {});
    this.itsmSiteContactsWithItsmId = this.itsmSiteContacts.filter(it => !!it.itsmContactId);
  }

  async loadItsmData() {
    // NOTE - Requires this.isLegacy and this.siteId to be set first
    // Preload the site information for this asset
    const itsmAddressInfo = await this.siteService.getItsmSettableAddresses(this.siteId);
    if (!this.error && itsmAddressInfo.errors && itsmAddressInfo.errors.length) {
      this.error = new Error(itsmAddressInfo.errors.join(".   "));
    }
    this.itsmAddressInfo = itsmAddressInfo;
    this.settableAddresses = itsmAddressInfo.settableAddresses;
    this.updateItsmAddressMapsHelper();

    this.itsmSiteContacts = await this.siteService.getItsmContacts(this.siteId);
    this.updateItsmContactMapsHelper();
    this.itsmDataReady = true;
  }

  @handleErrors()
  async loadData() {
    if (!this.siteId) {
      this.error = new Error("Site not found");
      return;
    }
    this.error = undefined;

    const {rows, count} = await this.siteDesignService.getCurrentSiteDesign(
      `${+(this.currentPage) - 1}`,
      `${+(this.pageSize)}`,
      this.searchText,
      this.siteId,
    );

    this.assets = rows;
    this.maxPages = Math.ceil(count / (+this.pageSize));
    this.totalCount = count;
  }

  async onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }

    await this.loadData();
  }

  async fileUploaded() {
    await this.loadData();
  }

  @handleErrors()
  async remove(asset: AssetForSiteDesignModel) {
    const doRemove = await this.confirmDialogService.confirm(
      "Confirm Removal",
      "Are you sure you want to remove this item?",
    );

    if (!doRemove) return;

    switch (asset.type) {
      case "asset":
        await this.assetService.deleteById(asset.id);
        break;
      case "accessory":
        await this.accessoryService.deleteAccessory(asset.id);
        break;
      default:
        throw new Error("Could not delete this item.");
    }

    await this.loadData();
  }

  @handleErrors()
  async edit(asset: AssetForSiteDesignModel) {
    switch (asset.type) {
      case "asset":
        if (asset.deviceKey) {
          const {addresses, contacts} = await this.assetService.editAsset({
            projectId: this.projectId,
            assetId: asset.id,
            addresses: this.settableAddressesWithItsmBuildingIds,
            contacts: this.itsmSiteContactsWithItsmId,
          });
          this.updateToReflectNewAddressOrContacts({addresses, contacts}); // Move any created addresses into the list
        } else {
          await this.assetRemovalService.editAsset(asset.id, this.projectId);
        }
        break;
      case "accessory":
        await this.accessoryService.editAccessory(asset.id);
        break;
      default:
        throw new Error("This item cannot be edited.");
    }

    await this.loadData();
  }

  async autoSave(): Promise<boolean> {
    await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);

    return true;
  }

  showDartCorrectionDialog() {
    return DartCorrectionRequestDialogComponent.showDialogModal(
      this.dialogService,
      {projectId: this.workflowService.workflowConfig.projectId},
    );
  }

  @handleErrors()
  async fixAccessory(asset: AssetForSiteDesignModel) {
    const siteId = this.workflowTargetInfo.targetId;

    await FixAccessoryDialogComponent.show(this.dialogService, asset.id, siteId, asset.serialNumber);

    await this.loadData();
  }
}
